import { useState, useEffect, useRef, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useClients, useArtists, useProjects } from "@/hooks/use-shared-queries";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { type Client, type User } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { getClientDisplayName } from "@/lib/utils";
import { useStatusConfigs } from "@/hooks/use-status-configs";
import { X, Clock, Search, Check } from "lucide-react";

interface AddProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultYearId?: string;
}

export function AddProjectDialog({ open, onOpenChange, defaultYearId }: AddProjectDialogProps) {
  const [selectedYear, setSelectedYear] = useState<string>("2025");
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [clientSearch, setClientSearch] = useState<string>("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientSearchRef = useRef<HTMLInputElement>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedNumber, setSelectedNumber] = useState<string>("");
  const [projectName, setProjectName] = useState<string>("");
  const [leadArtist, setLeadArtist] = useState<string>("none");
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [usedNumbers, setUsedNumbers] = useState<string[]>([]);
  const [timeTrackingEnabled, setTimeTrackingEnabled] = useState<boolean>(true);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Catalog-driven default project status (currently "new"). Used instead of a
  // hardcoded value so the default tracks whatever status is flagged isDefault.
  const { getDefaultStatus, isLoading: statusesLoading, isError: statusesError } = useStatusConfigs('project');

  // Use shared query hooks to prevent duplicate fetches
  const { data: clients = [] } = useClients();
  const { data: artists = [] } = useArtists();
  const { data: allProjects = [] } = useProjects();
  
  // Fetch time tracking settings to get default value
  const { data: timeTrackingSettings } = useQuery<{
    globalEnabled: boolean;
    defaultProjectEnabled: boolean;
  }>({
    queryKey: ['/api/time-tracking/settings'],
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Use defaultYearId if provided, otherwise fall back to current year
      const currentYear = new Date().getFullYear().toString();
      setSelectedYear(defaultYearId || currentYear);
      setSelectedClient("");
      setClientSearch("");
      setClientDropdownOpen(false);
      setSelectedNumber("");
      setProjectName("");
      setLeadArtist("none");
      setSelectedArtists([]);
      // Set time tracking based on default settings
      setTimeTrackingEnabled(timeTrackingSettings?.defaultProjectEnabled ?? true);
    }
  }, [open, timeTrackingSettings, defaultYearId]);

  // Update used numbers when year/client changes
  useEffect(() => {
    if (selectedYear && selectedClient) {
      const yearPrefix = selectedYear.slice(2, 4); // Get last 2 digits
      const usedInCombo = (allProjects as any[])
        .filter((p: any) => p.id.startsWith(`${yearPrefix}-${selectedClient}`))
        .map((p: any) => p.id.slice(5, 7));
      
      setUsedNumbers(usedInCombo);
    }
  }, [selectedYear, selectedClient, allProjects]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const search = clientSearch.toLowerCase();
    return clients.filter((client) => {
      const displayName = getClientDisplayName(client).toLowerCase();
      const companyName = (client.companyName || '').toLowerCase();
      const id = (client.id || '').toLowerCase();
      return displayName.includes(search) || companyName.includes(search) || id.includes(search);
    });
  }, [clients, clientSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(e.target as Node) &&
        clientSearchRef.current &&
        !clientSearchRef.current.contains(e.target as Node)
      ) {
        setClientDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectClient = (clientId: string) => {
    setSelectedClient(clientId);
    const client = clients.find(c => c.id === clientId);
    setClientSearch(client ? getClientDisplayName(client) : clientId);
    setClientDropdownOpen(false);
  };

  const createProjectMutation = useMutation({
    mutationFn: async (projectData: any) => {
      const response = await apiRequest('POST', '/api/projects', projectData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      toast({
        title: "Success",
        description: "Project created successfully"
      });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive"
      });
    }
  });

  const handleSave = async () => {
    if (!selectedYear || !selectedClient || !selectedNumber || !projectName.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    const yearCode = selectedYear.slice(2, 4); // Get last 2 digits
    const newId = `${yearCode}-${selectedClient}${selectedNumber}`;
    
    const projectData = {
      id: newId,
      yearId: yearCode, // Add the missing yearId field
      clientId: selectedClient,
      projectName: projectName.trim(),
      leadArtist: leadArtist === "none" ? "" : leadArtist,
      artists: selectedArtists,
      projectStatus: getDefaultStatus('project'),
      taskStatus: "empty",
      timeTrackingEnabled: timeTrackingEnabled
    };
    
    createProjectMutation.mutate(projectData);
  };

  const handleAddArtist = (artistId: string) => {
    if (!selectedArtists.includes(artistId)) {
      setSelectedArtists([...selectedArtists, artistId]);
    }
  };

  const handleRemoveArtist = (artistId: string) => {
    setSelectedArtists(selectedArtists.filter(id => id !== artistId));
  };

  const years = Array.from({ length: 8 }, (_, i) => (2025 + i).toString());
  const numbers = Array.from({ length: 99 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="navy-card border-gray-600 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">
            Add New Project
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Create a new project with ID, name, and team assignments
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Project ID Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Project ID</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Year</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600">
                    {years.map((year) => (
                      <SelectItem key={year} value={year} className="text-white">
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="relative">
                <Label className="text-gray-300">Client</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                  <Input
                    ref={clientSearchRef}
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setSelectedClient("");
                      setClientDropdownOpen(true);
                    }}
                    onFocus={() => setClientDropdownOpen(true)}
                    placeholder="Search clients..."
                    className="navy-bg border-gray-600 text-white pl-8"
                    autoComplete="off"
                  />
                </div>
                {clientDropdownOpen && (
                  <div
                    ref={clientDropdownRef}
                    className="absolute z-50 w-full mt-1 navy-card border border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto"
                  >
                    {filteredClients.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">No clients found</div>
                    ) : (
                      filteredClients.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client.id)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors flex items-center justify-between ${
                            selectedClient === client.id ? 'text-blue-400 bg-blue-950/30' : 'text-white'
                          }`}
                        >
                          <span>{getClientDisplayName(client)}</span>
                          {selectedClient === client.id && <Check className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-gray-300">Number (01-99)</Label>
                <Select value={selectedNumber} onValueChange={setSelectedNumber}>
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue placeholder="Select number" />
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600 max-h-48">
                    {numbers.map((number) => {
                      const isUsed = usedNumbers.includes(number);
                      return (
                        <SelectItem 
                          key={number} 
                          value={number} 
                          disabled={isUsed}
                          className={`text-white ${isUsed ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {number} {isUsed ? '(Used)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Project Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Project Details</h3>
            <div>
              <Label className="text-gray-300">Project Name *</Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                className="navy-bg border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Time Tracking Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Time Tracking</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-400" />
                <div>
                  <Label className="text-white">Enable Time Tracking</Label>
                  <p className="text-sm text-gray-400">
                    Allow time tracking for this project
                  </p>
                </div>
              </div>
              <Switch
                checked={timeTrackingEnabled}
                onCheckedChange={setTimeTrackingEnabled}
                className="data-[state=checked]:bg-blue-600"
              />
            </div>
          </div>

          {/* Team Assignment Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Team Assignment</h3>
            
            <div>
              <Label className="text-gray-300">Lead Artist</Label>
              <Select value={leadArtist} onValueChange={setLeadArtist}>
                <SelectTrigger className="navy-bg border-gray-600 text-white">
                  <SelectValue placeholder="Select lead artist (optional)" />
                </SelectTrigger>
                <SelectContent className="navy-card border-gray-600">
                  <SelectItem value="none" className="text-white">No lead artist</SelectItem>
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.shortCode || ''} className="text-white">
                      {artist.shortCode} - {artist.firstName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-300">Additional Artists</Label>
              <Select onValueChange={handleAddArtist}>
                <SelectTrigger className="navy-bg border-gray-600 text-white">
                  <SelectValue placeholder="Add artist to team" />
                </SelectTrigger>
                <SelectContent className="navy-card border-gray-600">
                  {artists
                    .filter(artist => !selectedArtists.includes(artist.shortCode || '') && (leadArtist === "none" || artist.shortCode !== leadArtist))
                    .map((artist) => (
                      <SelectItem key={artist.id} value={artist.shortCode || ''} className="text-white">
                        {artist.shortCode} - {artist.firstName} {artist.lastName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              
              {selectedArtists.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedArtists.map((artistId) => {
                    const artist = artists.find(a => a.shortCode === artistId);
                    return artist ? (
                      <Badge key={artistId} variant="secondary" className="bg-blue-900 text-white">
                        {artist.shortCode} - {artist.firstName} {artist.lastName}
                        <button
                          onClick={() => handleRemoveArtist(artistId)}
                          className="ml-2 hover:bg-red-600 rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="navy-card border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createProjectMutation.isPending || statusesLoading || statusesError}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createProjectMutation.isPending ? "Creating..." : "Create Project"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}